// pure helpers for the NCS color picker: hex parsing, code search matching, nearest-color-by-Lab-
// distance matching and the recent-colors localStorage cache. kept free of React/Redux so they're
// trivially testable.

import { NcsColorGet } from "src/components/ncs-colors/ncs-colors-types";

const RECENT_COLORS_STORAGE_KEY = "ncsColorPicker.recentColors";
const RECENT_COLORS_MAX = 8;

// converts a "#RRGGBB" (or shorthand "#RGB") hex string into an [r, g, b] tuple; missing/invalid
// channels fall back to 0 rather than NaN so callers can render something instead of crashing
export const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = hex.trim().replace(/^#/, "");
  const expanded = normalized.length === 3 ? normalized.replace(/(.)/g, "$1$1") : normalized;

  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);

  return [Number.isNaN(r) ? 0 : r, Number.isNaN(g) ? 0 : g, Number.isNaN(b) ? 0 : b];
};

// strips whitespace and the literal "NCS" prefix so both stored codes ("S 0502-B50G") and user
// search input ("0502b50g", "ncs 0502-b50g") normalize down to the same comparable string
const normalizeNcsSearchTerm = (value: string): string => value.trim().toUpperCase().replace(/\s+/g, "").replace(/^NCS/, "");

export const ncsCodeMatchesSearch = (code: string, searchTerm: string): boolean => {
  const normalizedTerm = normalizeNcsSearchTerm(searchTerm);
  if (!normalizedTerm) return true;
  return normalizeNcsSearchTerm(code).includes(normalizedTerm);
};

// sRGB (D65) reference white and the CIELAB f(t) piecewise-function constants, as defined by the
// CIE standard - see http://www.brucelindbloom.com/index.html?Eqn_RGB_XYZ_Matrix.html and
// http://www.brucelindbloom.com/index.html?Eqn_XYZ_to_Lab.html
const D65_REFERENCE_WHITE: [number, number, number] = [0.95047, 1, 1.08883];
const LAB_EPSILON = 216 / 24389; // (6/29)^3
const LAB_KAPPA = 24389 / 27; // (29/3)^3

// inverse sRGB gamma companding: converts a single 0-255 sRGB channel into its linear-light
// equivalent in [0, 1], the first step of sRGB -> XYZ -> Lab
const srgbChannelToLinear = (channel: number): number => {
  const normalized = channel / 255;
  return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
};

// CIELAB's piecewise cube-root-like function, applied to each XYZ component normalized against
// the D65 reference white
const labF = (t: number): number => (t > LAB_EPSILON ? Math.cbrt(t) : (LAB_KAPPA * t + 16) / 116);

// converts an [r, g, b] tuple (each 0-255, matching hexToRgb's return shape) into CIELAB [L, a, b]
// via linear sRGB -> CIE XYZ (D65 illuminant, sRGB primaries matrix) -> CIELAB. Lab space is used
// instead of RGB/HSL distance because Euclidean distance in Lab approximates human-perceived color
// difference much more closely, so "nearest" actually looks nearest to the user
export const rgbToLab = (rgb: [number, number, number]): [number, number, number] => {
  const [r, g, b] = rgb.map(srgbChannelToLinear);

  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
  const z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;

  const fx = labF(x / D65_REFERENCE_WHITE[0]);
  const fy = labF(y / D65_REFERENCE_WHITE[1]);
  const fz = labF(z / D65_REFERENCE_WHITE[2]);

  const l = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const labB = 200 * (fy - fz);

  return [l, a, labB];
};

const labDistanceSquared = (a: [number, number, number], b: [number, number, number]): number => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

// finds the catalogue entry whose color is closest (smallest Euclidean distance in Lab space) to
// the given rgb. This runs on every color-picker drag-move event, so converting all ~1,695
// catalogue entries to Lab on every call would be wasteful - callers on a hot path should precompute
// each entry's Lab value once (e.g. via useMemo keyed on the `colors` array) and pass it as
// `precomputedLabs`, in the same order as `colors`; only the picked `rgb` is then converted per call.
// When omitted, it's derived inline so the function still works correctly for one-off callers.
export const findNearestNcsColor = (colors: NcsColorGet[], rgb: [number, number, number], precomputedLabs?: Array<[number, number, number]>): NcsColorGet | undefined => {
  if (colors.length === 0) return undefined;

  const targetLab = rgbToLab(rgb);
  const labs = precomputedLabs ?? colors.map(color => rgbToLab(hexToRgb(color.hex)));

  let nearestColor = colors[0];
  let nearestDistance = labDistanceSquared(targetLab, labs[0]);

  for (let i = 1; i < colors.length; i++) {
    const distance = labDistanceSquared(targetLab, labs[i]);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestColor = colors[i];
    }
  }

  return nearestColor;
};

// recent colors are stored as a simple ordered array of NCS code strings, most-recent-first
export const getRecentNcsColorCodes = (): string[] => {
  try {
    const raw = window.localStorage.getItem(RECENT_COLORS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((code): code is string => typeof code === "string") : [];
  } catch {
    // localStorage can throw (private browsing, quota, corrupted JSON) - treat as "no recents"
    return [];
  }
};

export const addRecentNcsColorCode = (code: string): string[] => {
  const existing = getRecentNcsColorCodes().filter(existingCode => existingCode !== code);
  const updated = [code, ...existing].slice(0, RECENT_COLORS_MAX);

  try {
    window.localStorage.setItem(RECENT_COLORS_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // best-effort cache - ignore write failures
  }

  return updated;
};
